import { useEffect, useState } from "react";
import { Layout } from "../components/layout.tsx";
import { useAuth } from "../context/auth.tsx";
import { listSprints, activateSprint, completeSprint, type Sprint, type Page } from "../api/delivery.ts";

const STATUS_COLORS: Record<string, string> = {
  PLANNING: "bg-blue-900/50 text-blue-300",
  ACTIVE: "bg-green-900/50 text-green-300",
  COMPLETED: "bg-gray-700 text-gray-300",
  CANCELLED: "bg-red-900/30 text-red-400",
};

export function DeliverySprintsPage() {
  const { token } = useAuth();
  const [data, setData] = useState<Page<Sprint> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [acting, setActing] = useState<string | null>(null);

  const load = () => {
    if (token == null) return;
    setLoading(true);
    listSprints(token)
      .then(setData)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Failed"))
      .finally(() => setLoading(false));
  };

  useEffect(load, [token]);

  const act = async (id: string, fn: () => Promise<Sprint>) => {
    try {
      setActing(id);
      await fn();
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Action failed");
    } finally {
      setActing(null);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-white">Sprints</h1>
        {loading && <p className="text-gray-400">Loading…</p>}
        {error != null && <p className="text-red-400">{error}</p>}

        {data != null && (
          <div className="space-y-3">
            {data.items.map((sprint) => (
              <div key={sprint.id} className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-white font-medium">{sprint.name}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[sprint.status] ?? "bg-gray-700 text-gray-300"}`}>{sprint.status}</span>
                    </div>
                    {sprint.goal && <p className="text-gray-400 text-sm mt-1">{sprint.goal}</p>}
                    <div className="flex gap-4 mt-2 text-xs text-gray-500">
                      {sprint.startDate && <span>Start: {sprint.startDate.split("T")[0]}</span>}
                      {sprint.endDate && <span>End: {sprint.endDate.split("T")[0]}</span>}
                      {sprint.velocity != null && <span>Velocity: {sprint.velocity}</span>}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {sprint.status === "PLANNING" && (
                      <button disabled={acting != null} onClick={() => act(sprint.id, () => activateSprint(token!, sprint.id, sprint.version))} className="px-3 py-1 text-xs bg-green-700 hover:bg-green-600 text-white rounded transition-colors disabled:opacity-50">Activate</button>
                    )}
                    {sprint.status === "ACTIVE" && (
                      <button disabled={acting != null} onClick={() => act(sprint.id, () => completeSprint(token!, sprint.id, sprint.version))} className="px-3 py-1 text-xs bg-blue-700 hover:bg-blue-600 text-white rounded transition-colors disabled:opacity-50">Complete</button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {data.items.length === 0 && <p className="text-gray-400 text-sm">No sprints yet.</p>}
          </div>
        )}
      </div>
    </Layout>
  );
}
